import type { ComputedRef } from 'vue'

import { useDisciplineStore } from '@stores/discipline'
import { useCustomerStore } from '@stores/customer'

import { useDisciplineShowApi } from '@api/discipline/show.hook'
import { useDisciplineDetailsApi } from '@api/discipline/details.hook'
import { useCustomerShowApi } from '@api/customer/show.hook'
import { useDisciplinePrivileges } from '@hooks/discipline/discipline-privileges.hook'

export interface DisciplineContextOptions {
  /** Reread the discipline and its details even when they are loaded: after a write, or arriving from another page. */
  force?: boolean
  /** Reread the customer as well: the kid's sibling disciplines in the breadcrumbs come from it. */
  withCustomer?: boolean
}

interface DisciplineContextReturn {
  refIsLoading: ComputedRef<boolean>
  loadContext: (disciplineId: string, options?: DisciplineContextOptions) => Promise<void>
  whenReady: (disciplineId: string) => Promise<void>
}

/** Loads run one after another, so the answer for a discipline left behind can never land over the next one. */
let queue: Promise<void> = Promise.resolve()
let pending: { disciplineId: string, promise: Promise<void> } | null = null

/**
 * What every discipline tab shows — the header, the tab menu, the breadcrumbs and the details — read once and kept
 * while the admin moves between the tabs of the same discipline. Nothing here holds a navigation: callers that need
 * the data chain on `whenReady`.
 */
export const useDisciplineContext = (): DisciplineContextReturn => {
  const disciplineStore = useDisciplineStore()
  const customerStore = useCustomerStore()
  const { canViewDisciplines } = useDisciplinePrivileges()

  const refIsLoading = computed<boolean>(() => disciplineStore.isContextLoading)

  const isLoaded = (disciplineId: string): boolean => {
    const id = Number(disciplineId)
    const discipline = disciplineStore.getDiscipline

    return discipline?.id === id
      && (!canViewDisciplines.value || disciplineStore.getDetails?.id === id)
      && customerStore.getCustomer?.id === discipline.kid.customer_id
  }

  /** Another discipline's data must not stay on screen under the new route while its own is read. */
  const dropOtherDiscipline = (disciplineId: string): void => {
    const discipline = disciplineStore.getDiscipline
    if (!discipline || discipline.id === Number(disciplineId)) return

    disciplineStore.setDiscipline(null)
    disciplineStore.setDetails(null)
  }

  const read = async (disciplineId: string, options: DisciplineContextOptions): Promise<void> => {
    const id = Number(disciplineId)
    dropOtherDiscipline(disciplineId)

    const needsDiscipline = options.force || disciplineStore.getDiscipline?.id !== id
    const needsDetails = canViewDisciplines.value && (options.force || disciplineStore.getDetails?.id !== id)

    const [discipline] = await Promise.all([
      needsDiscipline ? useDisciplineShowApi({ disciplineId, silent: true }) : disciplineStore.getDiscipline,
      needsDetails ? useDisciplineDetailsApi({ disciplineId, silent: true }) : null,
    ])

    const customerId = discipline?.kid.customer_id
    if (!customerId) return

    if (options.withCustomer) disciplineStore.setCustomerGoId(null)

    if (options.withCustomer || customerStore.getCustomer?.id !== customerId) {
      await useCustomerShowApi({ customerId, silent: true })
    }
  }

  const loadContext = (disciplineId: string, options: DisciplineContextOptions = {}): Promise<void> => {
    const isReread = !!options.force || !!options.withCustomer

    if (!isReread && pending?.disciplineId === disciplineId) return pending.promise
    if (!isReread && !pending && isLoaded(disciplineId)) return Promise.resolve()

    dropOtherDiscipline(disciplineId)
    disciplineStore.setContextLoading(true)

    const promise = queue.then(() => read(disciplineId, options)).catch(() => undefined)
    queue = promise
    pending = { disciplineId, promise }

    void promise.finally(() => {
      if (pending?.promise !== promise) return

      pending = null
      disciplineStore.setContextLoading(false)
    })

    return promise
  }

  const whenReady = (disciplineId: string): Promise<void> =>
    pending?.disciplineId === disciplineId ? pending.promise : loadContext(disciplineId)

  return {
    refIsLoading,
    loadContext,
    whenReady,
  }
}
